"use client";

import styled from "styled-components";
import { useTranslations } from "next-intl";
import { useBoundStore } from "@/lib/stores/useBoundStore";
// import { useUserProfile } from "@/lib/hooks/useUser";
// import { useGetEnrollmentById } from "@/lib/hooks/useEnrollments";
// import { useGetPatientById } from "@/lib/hooks/usePatient";
// import { useAuthStore } from "@/lib/stores/useAuthStore";

const HealthcareTeamBody = styled.div`
  max-height: 75vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 25px;
  min-width: 350px;

  a {
    cursor: pointer;
  }

  a,
  span {
    font-weight: bold;
  }

  .title {
    font-weight: bold;
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .items {
    gap: 5px;
  }

  .provider span {
    color: ${(props) => props.theme.main};
  }

  .org span {
    color: ${(props) => props.theme.link};
  }

  @media (max-width: 425px) {
    max-height: 86vh;
    min-width: unset;
  }
`;

export function HealthcareTeamContent() {
  const t = useTranslations("healthcareTeam");
  // const user_profile = useAuthStore((state) => state.user_profile);
  // const userId = user_profile?.sub;
  const orgProfile = useBoundStore((state) => state.orgProfile);
  // const patientQuery = useGetPatientById(userId);
  // const patientQueryEnrollmentId = patientQuery.data?.enrollment_id || "";
  // const enrollmentQuery = useGetEnrollmentById(patientQueryEnrollmentId);
  // const { userProfileQuery } = useUserProfile(enrollmentQuery.data?.surgeon_id);

  const isIOS =
    typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !("MSStream" in window);
  const isAndroid = /android/i.test(navigator.userAgent);

  const handleAddress = (
    label: string,
    latitude: string,
    longitude: string
  ) => {
    let url = "";
    if (isIOS) {
      // For iOS devices - opens Apple Maps
      url = `maps://maps.apple.com/?q=${label}&ll=${latitude},${longitude}`;
      // Fallback to web version if app not available
      const fallbackUrl = `https://maps.apple.com/?q=${label}&ll=${latitude},${longitude}`;

      // Try to open the app, fallback to web
      window.location.href = url;
      setTimeout(() => {
        window.location.href = fallbackUrl;
      }, 25);
    } else if (isAndroid) {
      // For Android devices - opens Google Maps app or web
      url = `geo:${latitude},${longitude}?q=${latitude},${longitude}(${encodeURIComponent(label)})`;
      window.location.href = url;
    } else {
      // For desktop/other devices - opens Google Maps in browser
      url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(url, "_blank");
    }
  };

  // Get all keys from each category
  const addressKeys = Object.keys(orgProfile?.addresses || {});
  const phoneKeys = Object.keys(orgProfile?.phone_numbers || {});
  const emailKeys = Object.keys(orgProfile?.emails || {});
  const websiteKeys = Object.keys(orgProfile?.websites || {});

  // Find standalone items (keys that don't exist in addresses)
  const standalonePhones = phoneKeys.filter(
    (key) => !addressKeys.includes(key)
  );
  const standaloneEmails = emailKeys.filter(
    (key) => !addressKeys.includes(key)
  );
  const standaloneWebsites = websiteKeys.filter(
    (key) => !addressKeys.includes(key)
  );

  // Helper to get display name (orgProfile.name for "Main", otherwise the key)
  const getDisplayName = (key: string) => {
    return key === "Main" ? orgProfile?.name : key;
  };

  return (
    <HealthcareTeamBody>
      {/* Grouped sections - one per address key */}
      {addressKeys.map((key, idx) => {
        const address = orgProfile?.addresses?.[key];
        const phone = orgProfile?.phone_numbers?.[key];
        const email = orgProfile?.emails?.[key];
        const website = orgProfile?.websites?.[key];
        const coords = orgProfile?.coordinates?.[key];
        const latitude = coords?.latitude?.toString();
        const longitude = coords?.longitude?.toString();

        return (
          <div className="container" key={`group-${idx}`}>
            <p className="title">{getDisplayName(key)}</p>
            {address && (
              <p className="org">
                {t("address")}
                {": "}
                <a
                  onClick={() =>
                    latitude &&
                    longitude &&
                    handleAddress(key, latitude, longitude)
                  }
                  style={{
                    cursor: latitude && longitude ? "pointer" : "default",
                  }}
                >
                  <span>{address}</span>
                </a>
              </p>
            )}
            {phone && (
              <p className="org">
                {t("phone")}
                {": "}
                <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} target="_blank">
                  <span>{phone}</span>
                </a>
              </p>
            )}
            {email && (
              <p className="org">
                {t("email")}
                {": "}
                <a href={`mailto:${email}`} target="_blank">
                  <span>{email}</span>
                </a>
              </p>
            )}
            {website && (
              <p className="org">
                {t("website")}
                {": "}
                <a href={website} target="_blank">
                  <span>{website}</span>
                </a>
              </p>
            )}
          </div>
        );
      })}

      {/* Standalone Phone Numbers */}
      {standalonePhones.length > 0 && (
        <div className="container">
          <p className="title">{t("phoneNumbers")}</p>
          {standalonePhones.map((key, idx) => (
            <p className="org" key={`standalone-phone-${idx}`}>
              {key}
              <br />
              <a
                href={`tel:${orgProfile?.phone_numbers?.[key]?.replace(/[^\d+]/g, "")}`}
                target="_blank"
              >
                <span>{orgProfile?.phone_numbers?.[key]}</span>
              </a>
            </p>
          ))}
        </div>
      )}

      {/* Standalone Emails */}
      {standaloneEmails.length > 0 && (
        <div className="container">
          <p className="title">{t("emails")}</p>
          {standaloneEmails.map((key, idx) => (
            <p className="org" key={`standalone-email-${idx}`}>
              {key}
              <br />
              <a href={`mailto:${orgProfile?.emails?.[key]}`} target="_blank">
                <span>{orgProfile?.emails?.[key]}</span>
              </a>
            </p>
          ))}
        </div>
      )}

      {/* Standalone Websites */}
      {standaloneWebsites.length > 0 && (
        <div className="container">
          <p className="title">{t("websites")}</p>
          {standaloneWebsites.map((key, idx) => (
            <p className="org" key={`standalone-website-${idx}`}>
              {key}
              <br />
              <a href={orgProfile?.websites?.[key]} target="_blank">
                <span>{orgProfile?.websites?.[key]}</span>
              </a>
            </p>
          ))}
        </div>
      )}
    </HealthcareTeamBody>
  );
}

/* specifically if we show providers later on:

  {userProfileQuery.data && (
        <div className="container">
          <p className="title">{t("provider")}</p>
          <p className="provider">
            {t("email")}:{" "}
            <a href={`mailto:${userProfileQuery.data.email}`}>
              <span>{userProfileQuery.data.email}</span>
            </a>
          </p>
          {userProfileQuery.data.phone && (
            <p className="provider">
              {t("phone")}:{" "}
              <a href={`tel:${userProfileQuery.data.phone}`} target="_blank">
                <span>{userProfileQuery.data.phone}</span>
              </a>
            </p>
          )}
        </div>
      )} 
*/
